import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { secureFetch } from "../data/secureFetch";
import "./SystemAdminDashboard.css";

/**
 * System Admin Organization Detail View
 * Read-only view of a single organization with tabs
 */
function SystemAdminOrgDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [org, setOrg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [donations, setDonations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrgData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await secureFetch(
        `/api/system-admin/organizations/${orgId}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error("Failed to fetch organization");

      setOrg(response);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching organization:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [orgId]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await secureFetch(
        `/api/system-admin/organizations/${orgId}/campaigns`,
        { method: "GET" }
      );

      if (response.ok) {
        setCampaigns(response.campaigns);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  }, [orgId]);

  const fetchDonations = useCallback(async () => {
    try {
      const response = await secureFetch(
        `/api/system-admin/organizations/${orgId}/donations`,
        { method: "GET" }
      );

      if (response.ok) {
        setDonations(response.donations);
      }
    } catch (err) {
      console.error("Error fetching donations:", err);
    }
  }, [orgId]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await secureFetch(
        `/api/system-admin/organizations/${orgId}/members`,
        { method: "GET" }
      );

      if (response.ok) {
        setMembers(response.members);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  useEffect(() => {
    if (activeTab === "campaigns" && campaigns.length === 0) {
      fetchCampaigns();
    } else if (activeTab === "donations" && donations.length === 0) {
      fetchDonations();
    } else if (activeTab === "members" && members.length === 0) {
      fetchMembers();
    }
  }, [
    activeTab,
    fetchCampaigns,
    fetchDonations,
    fetchMembers,
    campaigns.length,
    donations.length,
    members.length,
  ]);

  if (loading) return <div className="loading">Loading organization...</div>;
  if (error) return <div className="error-message">Error: {error}</div>;
  if (!org) return <div className="error-message">Organization not found</div>;

  return (
    <div className="system-admin-page">
      <button
        onClick={() => navigate("/system-admin/organizations")}
        className="btn-back"
      >
        ← Back to Organizations
      </button>

      <div className="page-header">
        <h1>{org.name}</h1>
        <p className="page-subtitle">Read-only Organization View</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === "campaigns" ? "active" : ""}`}
          onClick={() => setActiveTab("campaigns")}
        >
          Campaigns
        </button>
        <button
          className={`tab ${activeTab === "donations" ? "active" : ""}`}
          onClick={() => setActiveTab("donations")}
        >
          Donations
        </button>
        <button
          className={`tab ${activeTab === "members" ? "active" : ""}`}
          onClick={() => setActiveTab("members")}
        >
          Members
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "overview" && (
          <div className="overview-section">
            <div className="info-grid">
              <div className="info-item">
                <label>Organization ID</label>
                <div className="info-value">{org.id}</div>
              </div>
              <div className="info-item">
                <label>EIN</label>
                <div className="info-value">{org.ein || "N/A"}</div>
              </div>
              <div className="info-item">
                <label>Email</label>
                <div className="info-value">{org.email || "N/A"}</div>
              </div>
              <div className="info-item">
                <label>External ID</label>
                <div className="info-value">{org.external_id || "N/A"}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "campaigns" && (
          <div className="data-table-section">
            <h2>Campaigns</h2>
            {campaigns.length === 0 ? (
              <p>No campaigns found</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Goal Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td>{campaign.id}</td>
                      <td>{campaign.name}</td>
                      <td>${campaign.goal_amount?.toFixed(2) || "0.00"}</td>
                      <td>
                        <span className={`status-badge ${campaign.status}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td>
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "donations" && (
          <div className="data-table-section">
            <h2>Donations</h2>
            {donations.length === 0 ? (
              <p>No donations found</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Donor</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((donation) => (
                    <tr key={donation.id}>
                      <td>{donation.id}</td>
                      <td>
                        {donation.first_name} {donation.last_name}
                      </td>
                      <td>
                        $
                        {(donation.donation_amount / 100)?.toFixed(2) || "0.00"}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${donation.status?.toLowerCase()}`}
                        >
                          {donation.status}
                        </span>
                      </td>
                      <td>{donation.donation_type || "N/A"}</td>
                      <td>
                        {new Date(donation.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "members" && (
          <div className="data-table-section">
            <h2>Members</h2>
            {members.length === 0 ? (
              <p>No members found</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>{member.email}</td>
                      <td>
                        {member.first_name} {member.last_name}
                      </td>
                      <td>
                        <span className={`role-badge ${member.role}`}>
                          {member.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${member.status}`}>
                          {member.status}
                        </span>
                      </td>
                      <td>
                        {new Date(member.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SystemAdminOrgDetail;
